import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import { useFirebase } from "firebase";
import { usePOST, NetworkError } from "hooks/usePublicAPI";
import AuthLayout from "layouts/AuthLayout";
import QuoteFlowLoadingPage from "pages/QuoteFlow/QuoteFlowLoadingPage";
import { safeRedirectPath } from "helpers/redirect";
import {
  DEAD_LINK_TITLE,
  deadLinkReasonOf,
  deadLinkShownFor,
  eventPurposeOf,
  linkFailureKindOf,
  linkSurfaceOf,
  parseGrantCompletion,
  purposeOfCompletion,
  quoteNotFoundPathOf,
  type DeadLinkKind,
} from "pages/Login/login-grant-flow";
import { linkFailurePlan } from "pages/Login/link-failure";
import { useTrackLeadFlowEvent } from "pages/StartLead/analytics";
import { leadFlowEvents, type LinkFailureKind } from "pages/StartLead/events";
import { reentryEvents } from "pages/QuoteFlow/reentry-events";
import {
  NOT_FOUND_COPY as QUOTE_NOT_FOUND_COPY,
  REENTRY_NOT_FOUND_STATE,
  REENTRY_PROOF_STATE,
} from "pages/QuoteFlow/reentry";
import { SupportCallLine } from "pages/Login/GrantParts";
import { accountChannelCopy } from "pages/StartLead/signIn";
import {
  VERIFY_NOT_FOUND_STATE,
  verifyNotFoundPathOf,
} from "pages/StartVerify/verify";
import { getFriendlyErrorMessage, getApiErrorMessage } from "utils/errors";

type VerifyTokenPayload = {
  token: string;
};
type VerifyTokenResponse = {
  success: boolean;
  custom_token?: string;
  purpose?: string;
  redirect_path?: string;
  next_step?: string;
  error?: string;
  error_code?: string;
};

export default function AuthTokenHandlerPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, signInWithCustomToken } = useFirebase();
  const trackEvent = useTrackLeadFlowEvent();

  const isMagicLinkLogin = location.pathname === "/auth/magic-link";

  const initialMessage = isMagicLinkLogin
    ? "Processing your login..."
    : "Verifying your request...";
  const successMessage = isMagicLinkLogin
    ? "Login successful! Redirecting..."
    : "Verification successful! Redirecting...";
  const errorTitle = isMagicLinkLogin ? "Login Failed" : "Verification Failed";
  const processName = isMagicLinkLogin ? "login" : "verification";

  const [statusMessage, setStatusMessage] = useState(initialMessage);
  const [isError, setIsError] = useState(false);
  const [deadLink, setDeadLink] = useState<{
    kind: DeadLinkKind;
    recoveryPath: string;
  } | null>(null);

  const { trigger: triggerVerifyToken } = usePOST<
    VerifyTokenResponse,
    NetworkError,
    VerifyTokenPayload
  >("/auth/verify");

  // A link token is single-use: verifying it twice (a StrictMode double
  // effect or a remount) spends it on the first call and fails the second.
  const verifyStartedRef = useRef(false);

  useEffect(() => {
    if (verifyStartedRef.current) return;
    verifyStartedRef.current = true;
    const token = searchParams.get("token");
    const redirectParam = searchParams.get("redirect");

    const failLink = (
      kind: LinkFailureKind,
      answeredPath: string | null = null,
    ) => {
      const plan = linkFailurePlan(kind, answeredPath ?? redirectParam);
      plan.events.forEach(trackEvent);
      if (plan.screen !== null) setDeadLink(plan.screen);
    };

    if (!token) {
      setStatusMessage(`Link is missing the required token.`);
      failLink("missing");
      setIsError(true);
      return;
    }
    trackEvent(
      leadFlowEvents.linkOpened({ purpose: eventPurposeOf(redirectParam) }),
    );

    const verifyAndLogin = async () => {
      setStatusMessage(initialMessage);
      setIsError(false);
      try {
        const response = await triggerVerifyToken({ token });
        const completion = parseGrantCompletion(response);

        const purpose = purposeOfCompletion(completion);
        const acceptLink = () => {
          if (purpose === null) return;
          trackEvent(
            leadFlowEvents.confirmAccepted({
              method: "email_link",
              purpose,
              surface: linkSurfaceOf(
                (completion.kind === "failed"
                  ? null
                  : completion.redirectPath) ?? redirectParam,
              ),
            }),
          );
          if (purpose === "quote_reentry") {
            trackEvent(reentryEvents.accepted({ method: "email_link" }));
          }
        };

        const quotePath = quoteNotFoundPathOf(completion);
        if (quotePath !== null) {
          acceptLink();
          navigate(quotePath, {
            replace: true,
            state: REENTRY_NOT_FOUND_STATE,
          });
          return;
        }
        const verifyPath = verifyNotFoundPathOf(completion);
        if (verifyPath !== null) {
          acceptLink();
          navigate(verifyPath, {
            replace: true,
            state: VERIFY_NOT_FOUND_STATE,
          });
          return;
        }
        if (completion.kind === "create_account") {
          acceptLink();
          navigate("/start", {
            replace: true,
            state: { signupToken: completion.signupToken },
          });
          return;
        }
        if (completion.kind === "not_found") {
          acceptLink();
          trackEvent(reentryEvents.notFound());
          setStatusMessage(QUOTE_NOT_FOUND_COPY);
          setIsError(true);
          return;
        }
        if (completion.kind === "use_account_channel") {
          acceptLink();
          setStatusMessage(accountChannelCopy());
          setIsError(true);
          return;
        }

        if (response?.success && response.custom_token) {
          setStatusMessage(successMessage);
          try {
            await signInWithCustomToken(auth, response.custom_token);
            acceptLink();
            // Honor a relative ?redirect= target (e.g. the set-password flow),
            // falling back to home.
            const destination = safeRedirectPath(
              searchParams.get("redirect") ??
                (completion.kind === "signed_in"
                  ? completion.redirectPath
                  : null),
            );
            const state =
              purpose === "quote_reentry" ? REENTRY_PROOF_STATE : undefined;
            setTimeout(
              () => navigate(destination, { replace: true, state }),
              500,
            );
          } catch (firebaseError: any) {
            console.error(
              `Firebase custom token sign-in failed during ${processName}:`,
              firebaseError,
            );
            Sentry.captureException(firebaseError);
            failLink("sign_in_failed");
            setStatusMessage(
              `${isMagicLinkLogin ? "Login" : "Process"} failed: ${getFriendlyErrorMessage(firebaseError.code)}`,
            );
            setIsError(true);
          }
        } else {
          const backendErrorCode = response?.error_code;
          const backendErrorMessage = response?.error;
          console.error(
            `Backend ${processName} process failed: Code: ${backendErrorCode}, Message: ${backendErrorMessage}`,
          );
          setStatusMessage(getApiErrorMessage(backendErrorCode));
          failLink(
            linkFailureKindOf(backendErrorCode),
            response?.redirect_path ?? null,
          );
          setIsError(true);
        }
      } catch (err) {
        let errorCode: string | undefined = undefined;
        let answeredPath: string | null = null;
        if (err instanceof NetworkError) {
          errorCode = (err.responseData as any)?.error_code;
          const path = (err.responseData as any)?.redirect_path;
          answeredPath = typeof path === "string" ? path : null;
        }
        const kind = linkFailureKindOf(errorCode);
        if (deadLinkShownFor(kind) === null) {
          console.error(`Error during ${processName} process:`, err);
          Sentry.captureException(err);
        }

        setStatusMessage(getApiErrorMessage(errorCode));
        failLink(kind, answeredPath);
        setIsError(true);
      }
    };

    verifyAndLogin();
  }, [
    searchParams,
    triggerVerifyToken,
    auth,
    signInWithCustomToken,
    trackEvent,
    navigate,
    initialMessage,
    successMessage,
    processName,
    isMagicLinkLogin,
  ]);

  if (isError && deadLink !== null) {
    return (
      <AuthLayout
        variant="quote_flow"
        title={DEAD_LINK_TITLE}
        description={deadLinkReasonOf(deadLink.kind) ?? ""}
      >
        <button
          type="button"
          className={`${CONTINUE_BUTTON_CLASS} w-full`}
          onClick={() => navigate(deadLink.recoveryPath, { replace: true })}
        >
          Continue
        </button>
      </AuthLayout>
    );
  }

  if (!isError) return <QuoteFlowLoadingPage />;

  return (
    <AuthLayout title={errorTitle} description={statusMessage}>
      <SupportCallLine kind="first_call" surface="login" screen="link_error" />
      <div className="mt-4 text-center">
        <button
          onClick={() => navigate("/login", { replace: true })}
          className="rounded bg-cantelope px-4 py-2 font-bold text-soil transition hover:bg-soil hover:text-cantelope"
        >
          Return to Login
        </button>
      </div>
    </AuthLayout>
  );
}
